/* eslint-disable react/forbid-dom-props */
/**
 * Main layout script class
 *
 * @package    Layouts
 * @author     Vadym Karpenko <vadym.karpenko@door3.com>
 */

import { loadBundles } from 'Actions/bundles';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
    Route,
    Switch,
} from 'react-router-dom';
import {
    TransitionGroup,
    CSSTransition,
} from 'react-transition-group';
import { injectIntl } from 'react-intl';
import { animateScroll } from 'react-scroll';
import {
    Offline,
    Online,
} from 'react-detect-offline';
import deepEqual from 'deep-equal';
import cx from 'classnames';
import styled from 'styled-components';

import 'Styles/main.scss';
import './styles.scss';

import {
    loadItem,
    setPreviewMode,
} from 'Actions/invite';
import { loadRegistration } from 'Actions/registration';
import Loader from 'Common/Loader';
import Modal from 'Common/Modal';
import NotificationList from 'Common/NotificationList';
import {
    LOCATION_PREFIX,
    RSVP_PROCESS_COMPANIONS,
    RSVP_PROCESS_DOCUMENTS,
    STATUS_ACCEPTED,
    STATUS_PENDING,
    STEP_BY_RSVP_PROCESS,
} from 'Constants';
import Tabs from 'Containers/Tabs';
import {
    inviteType,
    registrationType,
} from 'Types';
import { hexToRGBA } from 'Utils';

import OfflinePage from './OfflinePage.jsx';
import Header from './Header.jsx';
import Footer from './Footer';
import Banner from './Banner.jsx';

const Companions = React.lazy(() => import(/* webpackChunkName: 'companions' */ 'Layouts/Companions'));
const Complete = React.lazy(() => import(/* webpackChunkName: 'complete' */ 'Layouts/Complete'));
const Documents = React.lazy(() => import(/* webpackChunkName: 'documents' */ 'Layouts/Documents'));
const Error = React.lazy(() => import(/* webpackChunkName: 'error' */ 'Layouts/Error'));
const Landing = React.lazy(() => import(/* webpackChunkName: 'landing' */ 'Layouts/Landing'));
const NotFound = React.lazy(() => import(/* webpackChunkName: 'notFound' */ 'Layouts/NotFound'));
const Permission = React.lazy(() => import(/* webpackChunkName: 'permission' */ 'Layouts/Permission'));
const PersonalInformation = React.lazy(() => import(/* webpackChunkName: 'personal-information' */ 'Layouts/PersonalInformation'));
const Bundles = React.lazy(() => import(/* webpackChunkName: 'bundles' */ 'Layouts/Bundles'));

const BUTTON_SIZE = {
    large: {
        font: '18px',
        padding: '16px 32px',
    },
    medium: {
        font: '15px',
        padding: '13px 26px',
    },
    small: {
        font: '12px',
        padding: '10px 20px',
    },
};

const Container = styled.main`
${ ({ settings: { buttons, colors, fonts } }) => buttons && colors && fonts ? `
  background-color: ${ colors.background };
  color: ${ colors.secondaryText };
  font-family: '${ fonts.secondary }';

  .form-group.form-active label, .form-group .checkbox-input .cr .cr-icon {
    color: ${ colors.secondaryText } !important;
  }
  .form-group .toggle-input .switchery small, .maxlength-label {
    background-color: ${ colors.secondaryText } !important;
  }
  .form-group .form-control:focus {
    border-color: ${ colors.secondaryText } !important;
    box-shadow: 0 0 0 1px ${ hexToRGBA(colors.secondaryText, 0.5) } !important;
  }
  .form-group .checkbox-input .cr, .dropzone.active, .dropzone:focus {
    border-color: ${ colors.secondaryText } !important;
  }
  p, h1, h2, h3, h4, h5, h6, pre {
    color: ${ colors.secondaryText };
    font-family: '${ fonts.secondary }';
  }
  
  .registration-site-footer-html-block {
    color: ${ colors.secondaryText };
    font-family: '${ fonts.primary }';
  }
  
  .registration-tabs-item-link {
    font-family: '${ fonts.primary }';
    
    &.active {
      background-color: ${ colors.primaryText };
    }
  }

  .registration-site-banner h1, .registration-site-banner h5 {
    color: ${ colors.bannerText };
    font-family: ${ fonts.primary };
  }
  .registration-site-content-block-title {
    color: ${ colors.primaryText };
    font-family: '${ fonts.primary }';
    p, h1, h2, h3, h4, h5, h6, pre {
      color: ${ colors.primaryText };
      font-family: '${ fonts.primary }';
    }
  }
  .registration-site-header-link {
    color: ${ colors.secondaryText };
    font-family: '${ fonts.primary }';
    &:active, &:hover {
      color: ${ colors.primaryText };
    }
  }
  .btn {
    border-radius: ${ buttons.corners === 'rounded' ? '.25em' : 0 };
    padding: ${ BUTTON_SIZE[buttons.size].padding };
    font-size: ${ BUTTON_SIZE[buttons.size].font };
    font-family: '${ fonts.secondary }';
  }
  .btn-cta, .btn-primary {
    background-color: ${ buttons.type === 'outline' ? 'transparent' : colors.actionButton };
    border: 2px solid ${ colors.actionButton };
    color: ${ buttons.type === 'outline' ? colors.actionButton : 'white' };
    ${ buttons.hover ? `&:active, &:hover {
      background-color: ${ colors.actionButtonHover };
      border: 2px solid ${ colors.actionButtonHover };
      color: white;
    }` : '' }
    &:focus {
      box-shadow: 0 0 0 .2rem ${ hexToRGBA(colors.actionButton, 0.5) };
    }
  }
  .btn-outline-primary {
    background-color: ${ buttons.type !== 'outline' ? 'transparent' : colors.actionButton };
    border: 2px solid ${ colors.actionButton };
    color: ${ buttons.type !== 'outline' ? colors.actionButton : 'white' };
    ${ buttons.hover ? `&:active, &:hover {
      background-color: ${ colors.actionButtonHover };
      border: 2px solid ${ colors.actionButtonHover };
      color: white;
    }` : '' }
    &:focus {
      box-shadow: 0 0 0 .2rem ${ hexToRGBA(colors.actionButton, 0.5) };
    }
  }
  .btn-accept {
    background-color: ${ buttons.type === 'outline' ? 'transparent' : colors.acceptButton };
    border: 2px solid ${ colors.acceptButton };
    color: ${ buttons.type === 'outline' ? colors.acceptButton : 'white' };
    ${ buttons.hover ? `&:active, &:hover {
      background-color: ${ colors.acceptButtonHover };
      border: 2px solid ${ colors.acceptButtonHover };
      color: white;
    }` : '' }
    &:focus {
      box-shadow: 0 0 0 .2rem ${ hexToRGBA(colors.acceptButton, 0.5) };
    }
  }
  .btn-decline {
    background-color: ${ buttons.type === 'outline' ? 'transparent' : colors.declineButton };
    border: 2px solid ${ colors.declineButton };
    color: ${ buttons.type === 'outline' ? colors.declineButton : 'white' };
    ${ buttons.hover ? `&:active, &:hover {
      background-color: ${ colors.declineButtonHover };
      border: 2px solid ${ colors.declineButtonHover };
      color: white;
    }` : '' }
    &:focus {
      box-shadow: 0 0 0 .2rem ${ hexToRGBA(colors.declineButton, 0.5) };
    }
  }

  .card .card-title {
    color: ${ colors.primaryText } !important;
    font-family: '${ fonts.primary }';
  }
` : '' }`;

@connect(
    (store) => ({
        authorized: store.invite.authorized,
        bundles: store.bundles.data,
        failed: store.invite.failed || store.registration.failed,
        invite: store.invite.data,
        loaded: store.invite.loaded,
        loading: store.invite.loading || store.registration.loading,
        location: store.router.location,
        preview: store.invite.preview,
        registration: store.registration.data,
    }),
    (dispatch) => ({
        loadBundles: (params) => dispatch(loadBundles(params)),
        loadItem: (hash) => dispatch(loadItem(hash)),
        loadRegistration: (params) => dispatch(loadRegistration(params)),
        setPreviewMode: (preview) => dispatch(setPreviewMode(preview)),
    }),
)
@injectIntl
export default class Layout extends Component {
    /**
     * @type {Object}
     */
    static propTypes = {
        authorized: PropTypes.bool.isRequired,
        bundles: PropTypes.object,
        failed: PropTypes.bool.isRequired,
        history: PropTypes.object.isRequired,
        intl: PropTypes.object.isRequired,
        invite: inviteType,
        loadBundles: PropTypes.func.isRequired,
        loaded: PropTypes.bool.isRequired,
        loading: PropTypes.bool.isRequired,
        loadItem: PropTypes.func.isRequired,
        loadRegistration: PropTypes.func.isRequired,
        location: PropTypes.object.isRequired,
        match: PropTypes.object,
        preview: PropTypes.bool.isRequired,
        registration: registrationType,
        setPreviewMode: PropTypes.func.isRequired,
    };

    /**
     * @type {Object}
     */
    static defaultProps = {
        bundles: {},
        invite: null,
        match: undefined,
        registration: null,
    };

    /**
     * @type {Object}
     */
    state = {
        isBundlePage: false,
        isModalShown: false,
    };

    componentDidMount () {
        const {
            history,
            loadBundles,
            loadItem,
            loadRegistration,
            location: { pathname, query: { gid, inviteHash, preview }, search },
            setPreviewMode,
        } = this.props;

        loadRegistration({
            groupID: gid,
            inviteHash,
        });

        if (pathname === `${ LOCATION_PREFIX }/bundles`) {
            this.setState({ isBundlePage: true });
            loadBundles({ inviteHash });
        }

        loadItem({
            groupID: gid,
            inviteHash,
            preview,
        });
        if (!inviteHash && !gid) {
            history.replace({
                pathname: `${ LOCATION_PREFIX }/500`,
                search: search,
            });
        } else {
            if (pathname !== `${ LOCATION_PREFIX }/bundles`) {
                history.replace({
                    pathname: `${ LOCATION_PREFIX }/introduction`,
                    search: search,
                });
            }
        }
        if (preview) {
            setPreviewMode(true);
        }
        window.addEventListener('newVersionSW', this.showModal);
    }

    shouldComponentUpdate (nextProps, nextState) {
        if (this.state.isModalShown !== nextState.isModalShown) {
            return true;
        }

        if (this.props.authorized !== nextProps.authorized) {
            return true;
        }

        if (this.props.loaded !== nextProps.loaded) {
            return true;
        }

        if (this.props.loading !== nextProps.loading) {
            return true;
        }

        if (this.props.failed !== nextProps.failed) {
            return true;
        }

        if (!deepEqual(this.props.location, nextProps.location)) {
            return true;
        }

        if (!deepEqual(this.props.match, nextProps.match)) {
            return true;
        }

        if (!deepEqual(this.props.invite, nextProps.invite)) {
            return true;
        }

        if (!deepEqual(this.props.registration, nextProps.registration)) {
            return true;
        }

        return false;
    }

    componentDidUpdate (prevProps) {
        const {
            authorized,
            failed,
            history,
            invite,
            location: { pathname, query: { inviteHash }, search },
            preview,
            registration,
        } = this.props;

        if (invite && invite.rsvp === STATUS_ACCEPTED && invite.isItinerarySent) {
            window.location.href = `${ process.env.OLD_APP_HOST }/#/itinerary?inviteHash=${ inviteHash }`;
        }
        if (failed) {
            if (pathname !== `${ LOCATION_PREFIX }/500`) {
                history.replace({
                    pathname: `${ LOCATION_PREFIX }/500`,
                    search: search,
                });
            }

            return false;
        }
        if (!authorized && pathname !== `${ LOCATION_PREFIX }/permission`) {
            history.replace({
                pathname: `${ LOCATION_PREFIX }/permission`,
                search: search,
            });
        }
        if (!preview && authorized && pathname === `${ LOCATION_PREFIX }/permission`) {
            history.replace({
                pathname: `${ LOCATION_PREFIX }/introduction`,
                search: search,
            });
        }
        if (invite && invite.id && invite.rsvp !== STATUS_PENDING && pathname !== `${ LOCATION_PREFIX }/complete`) {
            if (pathname !== `${ LOCATION_PREFIX }/bundles`) {
                history.replace({
                    pathname: `${LOCATION_PREFIX}/complete`,
                    search: search,
                });
            }
        }
        if (invite && invite.rsvp && !prevProps.invite?.rsvp && invite.rsvp === STATUS_PENDING && [
            RSVP_PROCESS_COMPANIONS,
            RSVP_PROCESS_DOCUMENTS,
        ].includes(invite.rsvpProcess) && pathname === `${ LOCATION_PREFIX }/introduction`) {
            if (pathname !== `${ LOCATION_PREFIX }/bundles`) {
                history.replace({
                    pathname: `${ LOCATION_PREFIX }/${ STEP_BY_RSVP_PROCESS[invite.rsvpProcess] }`,
                    search: search,
                });
            }
        }
        if (invite && invite.rsvp && prevProps.invite && prevProps.invite.rsvp && invite.rsvp === STATUS_PENDING && invite.rsvpProcess !== prevProps.invite.rsvpProcess && STEP_BY_RSVP_PROCESS[invite.rsvpProcess]) {
            if (pathname !== `${ LOCATION_PREFIX }/bundles`) {
                history.replace({
                    pathname: `${LOCATION_PREFIX}/${STEP_BY_RSVP_PROCESS[invite.rsvpProcess]}`,
                    search: search,
                });
            }
        }

        if (pathname !== prevProps.location.pathname) {
            animateScroll.scrollToTop();
            this.executeScripts();
        }
        if (!prevProps.registration.id && registration.id) {
            this.fontsLink && document.head.removeChild(this.fontsLink);
            this.loadFonts([
                registration.fonts.primary,
                registration.fonts.secondary,
            ]);
            if (registration.customCode) {
                this.addCustomCode(registration.customCode.header, document.head);
                this.addCustomCode(registration.customCode.body, document.body);
            }
            this.executeScripts();
        }
    }

    componentWillUnmount () {
        this.fontsLink && document.head.removeChild(this.fontsLink);
        window.removeEventListener('newVersionSW', this.showModal);
    }

    fontsLink = null;

    loadFonts = (fonts) => {
        this.fontsLink = document.createElement('link');
        this.fontsLink.rel = 'stylesheet';
        this.fontsLink.href = `https://fonts.googleapis.com/css?family=${ [...new Set(fonts)].join('|') }&display=swap`;

        document.head.appendChild(this.fontsLink);
    };

    showModal = () => {
        this.setState({
            isModalShown: true,
        });
    };

    closeModal = () => {
        this.setState({
            isModalShown: false,
        });
    };

    updateVersion = () => {
        window.dispatchEvent(new Event('forceRefreshSW'));
    };

    /**
     * Add script node to target
     *
     * @param {HTMLElement} node
     * @param {HTMLElement} target
     */
    addScript = (node, target) => {
        const script = document.createElement('script');

        script.innerHTML = node.innerHTML;
        node.attributes.forEach((attribute) => {
            script.setAttribute(attribute.nodeName, attribute.nodeValue);
        });
        target.appendChild(script);
    };

    /**
     * Add custom code to target
     *
     * @param {string} content
     * @param {HTMLElement} target
     */
    addCustomCode = (content, target) => {
        if (!content || !target) {
            return;
        }

        const wrapper = document.createElement('div');

        wrapper.innerHTML = content.trim();
        wrapper.childNodes.forEach((node) => {
            if (node.nodeName === 'SCRIPT') {
                this.addScript(node, target);
            } else {
                target.appendChild(node);
            }
        });
    };

    executeScripts = () => {
        setTimeout(() => {
            const scripts = document.querySelectorAll('.registration-site-content-block-text script');

            scripts.forEach((script) => {
                if (!script.src) {
                    window.eval(script.innerHTML);
                } else {
                    this.addScript(script, document.body);
                }
            });
        }, 300);
    };

    render () {
        const {
            bundles,
            failed,
            intl,
            invite,
            loaded,
            loading,
            location,
            registration,
        } = this.props;
        const { isBundlePage, isModalShown } = this.state;

        return (
            <main
                className={ cx('registration-site') }
            >
                { loading && <Loader global/> }
                <Container
                    className="registration-site-container"
                    settings={ registration }
                >
                    { !registration?.headerDisabled && !isBundlePage && <Header
                        logo={ registration?.logo }
                        name={ invite?.data?.name }
                        supportInfo={ invite?.supportInfo }
                    /> }

                    {!isBundlePage && <Banner
                        banner={ registration?.banner }
                        data={ invite?.data }
                        settings={ registration?.bannerSettings }
                    />}

                    {isBundlePage && <Banner
                        banner={ bundles?.banner }
                        data={ {} }
                        settings={ bundles?.bannerSettings }
                    />}

                    <Online>
                        { !failed && !isBundlePage && <Tabs pathname={ location.pathname } search={ location.search }/> }
                        <TransitionGroup>
                            <CSSTransition
                                key={ location.key }
                                classNames="fade"
                                timeout={ 350 }
                            >
                                <React.Suspense fallback={ <Loader global/> }>
                                    { loaded && <Switch key={ location.pathname } location={ location }>
                                        <Route
                                            exact path={ `${ LOCATION_PREFIX }/permission` } component={ Permission }
                                        />
                                        <Route
                                            exact
                                            path={ `${ LOCATION_PREFIX }/introduction` }
                                            component={ Landing }
                                        />
                                        <Route
                                            exact
                                            path={ `${ LOCATION_PREFIX }/bundles` }
                                            component={ Bundles }
                                        />
                                        <Route
                                            exact
                                            path={ `${ LOCATION_PREFIX }/personal-information` }
                                            component={ PersonalInformation }
                                        />
                                        <Route
                                            exact path={ `${ LOCATION_PREFIX }/companions` } component={ Companions }
                                        />
                                        <Route exact path={ `${ LOCATION_PREFIX }/documents` } component={ Documents }/>
                                        <Route exact path={ `${ LOCATION_PREFIX }/complete` } component={ Complete }/>

                                        <Route exact path={ `${ LOCATION_PREFIX }/404` } component={ NotFound }/>
                                        <Route exact path={ `${ LOCATION_PREFIX }/500` } component={ Error }/>
                                    </Switch> }
                                </React.Suspense>
                            </CSSTransition>
                        </TransitionGroup>
                    </Online>
                    <Offline>
                        <OfflinePage/>
                    </Offline>

                    { !registration?.footerDisabled && <Footer text={ registration?.footerText }/> }

                    <NotificationList/>
                    <Modal
                        size="md"
                        close={ this.closeModal }
                        description={ intl.formatMessage({ id: 'labels.newVersionDescription' }) }
                        title={ intl.formatMessage({ id: 'labels.newVersion' }) }
                        show={ isModalShown }
                        submit={ this.updateVersion }
                        submitLabel="updateModal"
                    />
                </Container>
            </main>
        );
    }
}
